import React from "react";
import { Controller, Control } from "react-hook-form";
import { t } from "i18next";
import classNames from "classnames/bind";
import cln from "classnames";
import { CartFormValues } from "./types";
import { CustomBottomSheet } from "../../../components/UI/CustomBottomSheet";
import { Capacitor } from "@capacitor/core";
import { IonInput } from "@ionic/react";
import { motion } from "framer-motion";
import styles from "../../../components/InputBottomSheet/InputBottomSheet.module.scss";
import { ReactComponent as PencilIcon } from "../../../assets/icon/pencil.svg";
import { useOrderData } from "../../../providers/orderDataStore";
import { useCartData } from "../../../providers/cartDataStore";
import { useGetMinimumOrderAmount } from "../../../hooks";
import { useGetDeliveryShippingFeePerKg } from "../../../hooks/useGetDeliveryShippingFeePerKg";

const cx = classNames.bind(styles);

interface CashGivenBottomSheetProps {
  control: Control<CartFormValues>;
}

const denomination = [500, 1000, 1500, 2000, 5000];

const parseCashGiven = (raw: string | null | undefined) => (raw ? Number(raw) || null : null);

export const CashGivenBottomSheet = ({ control }: CashGivenBottomSheetProps) => {
  const isIos = Capacitor.getPlatform() === "ios";
  const { cartProducts } = useCartData();
  const cartItems = cartProducts.length;
  const { shippingMethod } = useOrderData();
  const currentShippingCost = shippingMethod?.cost ?? "0";
  const shippingCost = parseInt(currentShippingCost, 10) || 0;

  const { isMinimumAmountReached } = useGetMinimumOrderAmount();
  const { cartTotalValue } = useCartData();
  const { rewardPoints } = useCartData();
  const feePerKg = useGetDeliveryShippingFeePerKg();

  const orderTotalWithShipping = isMinimumAmountReached
    ? (cartTotalValue ?? 0) + feePerKg + shippingCost
    : (cartTotalValue ?? 0);

  const payableAmount = Math.max(0, orderTotalWithShipping - (rewardPoints ?? 0));

  const { updateOrderData } = useOrderData();

  const renderPreview = ({ changeAmount, value }: { changeAmount: number | null; value: number | null }) => {
    let subtitle = t("Enter amount for change");

    if (typeof value === "number" && value > 0) {
      if (!cartItems) {
        subtitle = t("Cart is empty");
      } else if (changeAmount) {
        subtitle = t("Change to give back", { amount: changeAmount });
      } else if (value < payableAmount) {
        subtitle = t("Cash given is less than order total");
      } else {
        subtitle = t("Exact change");
      }
    }

    return (
      <div className="flex items-center justify-between px-2.5 py-4 border-b border-gray-200 min-h-[4.375rem]">
        <div className="flex flex-col w-[95%]">
          <div className="text-[#292929]">{t("Cash change")}</div>
          <div className="text-xs text-[#656565]">{subtitle}</div>
        </div>
        <PencilIcon className="text-sm font-bold w-4 h-4 text-[#D5D5D5]" />
      </div>
    );
  };

  return (
    <Controller
      name="cash_given"
      control={control}
      render={({ field: { value, onChange } }) => {
        const changeAmount = typeof value === "number" ? Math.max(0, value - payableAmount) : null;

        return (
          <CustomBottomSheet
            title={t("Cash calculation")}
            footerTitle={t("Save")}
            renderPreview={() => renderPreview({ changeAmount, value })}
            contentClassName={cx("content")}
            snapPoints={({ minHeight }) => [isIos ? minHeight + 50 : minHeight]}
          >
            <IonInput
              inputMode="numeric"
              value={value ?? ""}
              placeholder={t("Enter amount...")}
              className="bg-[#F0F0F0] rounded-[10px] !px-2.5 !pt-0 text-xs"
              onIonChange={(e) => {
                const nextCashGiven = parseCashGiven(e.detail.value);
                onChange(nextCashGiven);
                updateOrderData({ cash_given: nextCashGiven });
              }}
            />
            <div className="w-full flex flex-wrap gap-2">
              {denomination.map((amount) => {
                const isActive = amount === value;

                if (payableAmount > amount) {
                  return;
                }

                return (
                  <motion.button
                    type="button"
                    key={amount}
                    onClick={() => {
                      onChange(amount);
                      updateOrderData({ cash_given: amount });
                    }}
                    whileTap={{ scale: 0.96 }}
                    className={cln(
                      "relative px-2.5 py-1 rounded-[10px] w-[70px] text-black my-2.5",
                      "border border-solid transition-colors duration-300 ease-in-out",
                      isActive ? "border-[#fed700]" : "border-gray-500 bg-white",
                    )}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="cash-denomination-active"
                        className="absolute inset-0 rounded-[10px] bg-[#fed700] border border-[#fed700]"
                        transition={{ type: "spring", stiffness: 380, damping: 28 }}
                      />
                    )}
                    <span className="relative z-10">{amount}</span>
                  </motion.button>
                );
              })}
            </div>
          </CustomBottomSheet>
        );
      }}
    />
  );
};
